import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  ChevronLeft,
  Mail,
  Phone,
  Edit2,
  X,
  Loader2,
  Lock,
  Shield,
  UserPlus,
  Users,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useMatter } from '../hooks/useMatter';
import ClientVisasPanel from '../components/ClientVisasPanel';

interface ContactRow {
  id: string;
  given_names: string | null;
  family_name: string | null;
  email: string;
  phone: string | null;
  contact_type: string;
  staff_role: string | null;
  date_of_birth: string | null;
}

interface MatterEntry {
  id: string;
  matter_id: string | null;
  visa_subclass: string;
  variant: string | null;
  status: string;
  lodgement_date: string | null;
  updated_at: string;
  /** This contact's role on this matter, in source priority. */
  role: 'applicant' | 'sponsor' | 'dependant' | 'witness' | 'staff_assignee' | 'collaborator';
  /** Primary applicant on this matter, useful when this contact is sponsor/dependent. */
  primary_name: string;
}

export default function ContactProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { actuallyStaff } = useMatter();

  const [contact, setContact] = useState<ContactRow | null>(null);
  const [matters, setMatters] = useState<MatterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: c, error: cErr } = await supabase
          .from('contacts')
          .select('id, given_names, family_name, email, phone, contact_type, staff_role, date_of_birth')
          .eq('id', id)
          .maybeSingle();
        if (cErr) throw cErr;
        if (!c) throw new Error('Contact not found');
        if (!cancelled) setContact(c as ContactRow);

        // Pull matters from three sources to capture every role this contact has:
        //   - matters.primary_contact_id  → role 'applicant'
        //   - matters.sponsor_contact_id  → role 'sponsor'
        //   - matter_contacts.contact_id  → role from join (dependant, witness, etc.)
        // Then dedupe by matter id; if a contact appears in multiple sources we
        // keep the first match (priority: applicant > sponsor > join-table).
        const [primaryR, sponsorR, mcR] = await Promise.all([
          supabase
            .from('matters')
            .select(
              `id, matter_id, visa_subclass, variant, status, lodgement_date, updated_at,
               primary_contact:contacts!matters_primary_contact_id_fkey (given_names, family_name)`,
            )
            .eq('primary_contact_id', id)
            .is('deleted_at', null),
          supabase
            .from('matters')
            .select(
              `id, matter_id, visa_subclass, variant, status, lodgement_date, updated_at,
               primary_contact:contacts!matters_primary_contact_id_fkey (given_names, family_name)`,
            )
            .eq('sponsor_contact_id', id)
            .is('deleted_at', null),
          supabase
            .from('matter_contacts')
            .select(
              `role,
               matter:matters!matter_contacts_matter_id_fkey (
                 id, matter_id, visa_subclass, variant, status, lodgement_date, updated_at, deleted_at,
                 primary_contact:contacts!matters_primary_contact_id_fkey (given_names, family_name)
               )`,
            )
            .eq('contact_id', id),
        ]);

        if (cancelled) return;

        type RawMatter = {
          id: string;
          matter_id: string | null;
          visa_subclass: string;
          variant: string | null;
          status: string;
          lodgement_date: string | null;
          updated_at: string;
          deleted_at?: string | null;
          primary_contact: { given_names: string | null; family_name: string | null } | null;
        };

        const merged = new Map<string, MatterEntry>();
        const pushMatter = (raw: RawMatter | null, role: MatterEntry['role']) => {
          if (!raw || raw.deleted_at) return;
          if (merged.has(raw.id)) return;
          const primary_name =
            [raw.primary_contact?.given_names, raw.primary_contact?.family_name]
              .filter(Boolean)
              .join(' ')
              .trim() || 'Unnamed';
          merged.set(raw.id, {
            id: raw.id,
            matter_id: raw.matter_id,
            visa_subclass: raw.visa_subclass,
            variant: raw.variant,
            status: raw.status,
            lodgement_date: raw.lodgement_date,
            updated_at: raw.updated_at,
            role,
            primary_name,
          });
        };

        for (const m of (primaryR.data ?? []) as unknown as RawMatter[]) pushMatter(m, 'applicant');
        for (const m of (sponsorR.data ?? []) as unknown as RawMatter[]) pushMatter(m, 'sponsor');
        for (const row of (mcR.data ?? []) as unknown as { role: MatterEntry['role']; matter: RawMatter | null }[]) {
          pushMatter(row.matter, row.role);
        }

        const list = Array.from(merged.values()).sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
        if (!cancelled) setMatters(list);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load contact');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Staff-only: clients hitting this URL would see other clients' data.
  if (!actuallyStaff) {
    return (
      <div className="card p-6 text-sm text-slate-600">
        This page is not available.
      </div>
    );
  }

  if (loading) {
    return <div className="text-sm text-slate-500">Loading contact...</div>;
  }
  if (error || !contact) {
    return (
      <div className="card p-6 text-sm text-red-600">
        Couldn't load contact: {error ?? 'not found'}
      </div>
    );
  }

  const fullName =
    [contact.given_names, contact.family_name].filter(Boolean).join(' ').trim() || contact.email;

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Link
        to="/matters"
        className="text-xs text-slate-500 hover:text-[#0B2341] inline-flex items-center gap-1"
      >
        <ChevronLeft size={12} /> All matters
      </Link>

      {/* Identity header */}
      <div className="card p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-[#EBF7FD] text-[#1A8DC7] grid place-items-center font-bold text-lg shrink-0">
            {(contact.given_names ?? contact.email)[0]?.toUpperCase()}
            {(contact.family_name ?? '')[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-[#0B2341]">{fullName}</h1>
              <RoleBadge type={contact.contact_type} />
            </div>
            <div className="mt-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-sm">
              <a
                href={`mailto:${contact.email}`}
                className="text-slate-700 hover:text-[#2CABEA] inline-flex items-center gap-1.5"
              >
                <Mail size={14} className="text-slate-400" /> {contact.email}
              </a>
              {contact.phone && (
                <a
                  href={`tel:${contact.phone}`}
                  className="text-slate-700 hover:text-[#2CABEA] inline-flex items-center gap-1.5"
                >
                  <Phone size={14} className="text-slate-400" /> {contact.phone}
                </a>
              )}
              {contact.date_of_birth && (
                <span className="text-slate-500 text-xs">
                  DOB {formatDate(contact.date_of_birth)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="btn-secondary !py-1.5 !px-3 !text-xs shrink-0"
          >
            <Edit2 size={12} /> Edit details
          </button>
        </div>
      </div>

      {/* Matters list */}
      <Section
        title="Matters"
        count={matters.length}
        action={
          <button
            type="button"
            onClick={() => navigate(`/matters/new?contact=${contact.id}`)}
            className="text-xs text-[#2CABEA] hover:underline inline-flex items-center gap-1"
          >
            <UserPlus size={12} /> New matter for this client
          </button>
        }
      >
        {matters.length === 0 ? (
          <div className="text-sm text-slate-500">
            No matters yet. Click "New matter for this client" to start one.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 -mx-4 -my-4">
            {matters.map((m) => (
              <li key={m.id}>
                <Link
                  to={`/matters/${m.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-500">
                        {m.matter_id ?? '-'}
                      </span>
                      <span className="font-semibold text-[#0B2341]">
                        {m.visa_subclass}
                        {m.variant ? ` ${m.variant}` : ''}
                      </span>
                      <MatterRoleChip role={m.role} primaryName={m.primary_name} />
                      <StatusChip status={m.status} />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-x-3 flex-wrap">
                      <span>Last update {relativeTime(m.updated_at)}</span>
                      {m.lodgement_date && (
                        <span>
                          Lodgement{' '}
                          <span className="font-medium text-[#0B2341]">
                            {formatDate(m.lodgement_date)}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRight
                    size={14}
                    className="text-slate-300 group-hover:text-[#2CABEA] transition-colors shrink-0"
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Visa records (reuses ClientVisasPanel in standalone mode — no current matter context) */}
      <ClientVisasPanel contactId={contact.id} isStaff={actuallyStaff} currentMatterId={null} />

      {editing && (
        <EditContactModal
          contact={contact}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setContact((prev) => (prev ? { ...prev, ...updated } : prev));
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function Section({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
          {title}
          {typeof count === 'number' && (
            <span className="ml-1.5 font-normal text-slate-400">· {count}</span>
          )}
        </h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function RoleBadge({ type }: { type: string }) {
  const lower = type.toLowerCase();
  if (lower === 'staff') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#EBF7FD] text-[#1A8DC7]">
        <Shield size={11} /> Staff
      </span>
    );
  }
  if (lower === 'sponsor') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-purple-100 text-purple-700">
        Sponsor
      </span>
    );
  }
  if (lower === 'dependant') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700">
        <Users size={11} /> Dependent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700">
      Applicant
    </span>
  );
}

function MatterRoleChip({
  role,
  primaryName,
}: {
  role: MatterEntry['role'];
  primaryName: string;
}) {
  const label =
    role === 'applicant'
      ? 'Applicant'
      : role === 'sponsor'
      ? `Sponsoring ${primaryName}`
      : role === 'dependant'
      ? `Dependent of ${primaryName}`
      : role.replace(/_/g, ' ');
  return (
    <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
      {label}
    </span>
  );
}

function StatusChip({ status }: { status: string }) {
  const lower = status.toLowerCase();
  let cls = 'bg-slate-100 text-slate-700';
  if (lower.includes('block')) cls = 'bg-red-100 text-red-700';
  else if (lower.includes('await')) cls = 'bg-amber-100 text-amber-800';
  else if (lower.includes('progress')) cls = 'bg-blue-100 text-blue-700';
  else if (lower.includes('lodge') || lower.includes('grant')) cls = 'bg-green-100 text-green-700';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
      {status}
    </span>
  );
}

function EditContactModal({
  contact,
  onClose,
  onSaved,
}: {
  contact: ContactRow;
  onClose: () => void;
  onSaved: (updated: Partial<ContactRow>) => void;
}) {
  const [givenNames, setGivenNames] = useState(contact.given_names ?? '');
  const [familyName, setFamilyName] = useState(contact.family_name ?? '');
  const [phone, setPhone] = useState(contact.phone ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const patch = {
        given_names: givenNames.trim() || null,
        family_name: familyName.trim() || null,
        phone: phone.trim() || null,
      };
      const { error } = await supabase.from('contacts').update(patch).eq('id', contact.id);
      if (error) throw error;
      onSaved(patch);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 grid place-items-center px-4">
      <form onSubmit={submit} className="card w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#0B2341]">Edit contact</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-500">
            Given names
            <input
              type="text"
              value={givenNames}
              onChange={(e) => setGivenNames(e.target.value)}
              className="mt-1 input"
            />
          </label>
          <label className="text-xs text-slate-500">
            Family name
            <input
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              className="mt-1 input"
            />
          </label>
        </div>
        <label className="text-xs text-slate-500 block">
          Phone
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 input"
          />
        </label>
        <label className="text-xs text-slate-500 block">
          Email
          <div className="mt-1 flex items-center gap-1.5 input bg-slate-50 text-slate-600 cursor-not-allowed select-none">
            <Lock size={12} className="text-slate-400 flex-shrink-0" />
            <span className="truncate">{contact.email}</span>
          </div>
        </label>
        <div className="text-xs text-slate-400">
          The email is locked. It arrives with the client's account and is what they sign in with, so
          it can't be changed here. Reach out if a client's email genuinely needs updating.
        </div>

        {err && <div className="text-xs text-red-600">{err}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary !py-1.5 !px-3 !text-xs">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary !py-1.5 !px-3 !text-xs disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : null} Save
          </button>
        </div>
      </form>
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.round((now - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}
